import { Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { EsriExpand } from '../../../config/esri.module';
import { MainMapService } from '../../../shared/service/main-map.service';
import { DomUtilService } from '../../../shared/service/dom-util.service';
import { WidgetNotifyService } from '../../../shared/service/widget-notify.service';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { TypeaheadService } from '../../../shared/service/typeahead.service';
import { Observable } from 'rxjs';
import { LayerContainerService } from '../../../shared/service/layer-container.service';
import { QueryService } from '../../../shared/service/query.service';
import { ConfigService } from '../../../config/config.service';
import { MessageService } from '../../../core/service/message.service';
import { CONSTANTS, CONSTANTS_VALUE } from '../../../config/constants.module';
import { mergeMap } from 'rxjs/operators';
import { SearchInfo } from '../../../config/model/search-config.model';
import { BaseWidget } from '../base-widget/base-widget';
import {SelectionService} from "../../../shared/service/selection.service";

@Component({
    selector: 'search-parzelle',
    templateUrl: './search-parzelle.component.html',
    styleUrls: ['./search-parzelle.component.scss'],
    standalone: false
})
export class SearchParzelleComponent extends BaseWidget implements OnInit {
  @ViewChild('searchParzelleComponentRef', { static: true })
  searchParzelleComponentRef: ElementRef;
  @ViewChild('gemeindeRef', { static: true }) gemeindeRef: ElementRef;
  @ViewChild('parzelleRef', { static: true }) parzelleRef: ElementRef;

  public formGroup: UntypedFormGroup;
  public searchTextGemeinde: string;
  public searchTextParzelleNr: string;
  public searchListGemeinde: Observable<any>;
  public searchListParzellenNr: Observable<any>;
  public searchConfig: SearchInfo;

  constructor(
    public mainMapService: MainMapService,
    public widgetNotifyService: WidgetNotifyService,
    private _messageService: MessageService,
    public _domUtilService: DomUtilService,
    public _typeaheadService: TypeaheadService,
    private _layerContainerService: LayerContainerService,
    private _configService: ConfigService,
    private _queryService: QueryService,
    private selectionService: SelectionService,
    @Inject(CONSTANTS) private CONST: any
  ) {
    super(mainMapService, widgetNotifyService);
  }

  ngOnInit() {
    // get MainMapService
    const mapView: any = this.mainMapService.mapView;

    // Create an Expand instance and set the content
    const expandProperties: __esri.ExpandProperties = {
      view: mapView,
      id: this.id,
      content: this.searchParzelleComponentRef.nativeElement,
      expandIcon: 'search',
    };

    this.expandWidget = new EsriExpand(expandProperties);

    // add widget to the main map
    mapView.ui.add(this.expandWidget, 'top-right');

    this.initWidgetListeners();
    // create controls
    this.formGroup = new UntypedFormGroup({
      taGemeinde: new UntypedFormControl(),
      taParzelle: new UntypedFormControl(),
    });

    this.initTypeAheadSearch();
  }


  /* Search methods
   ========================== */

  /**
   * @name initTypeAheadSearch
   * @description defines the search source and the search query
   */
  private initTypeAheadSearch() {
    const hSearchInfo = this.getSearchConfig();

    if (!hSearchInfo.headSearchField) {
      throw new Error('there must be a searchField defined in the config');
    }
    const _layer = this._layerContainerService.layerList.filter(
      f => f.esriLayer.id === hSearchInfo.fkLayer
    )[0];

    // define search list Gemeinde
    this.searchListGemeinde = new Observable((observer: any) => {
      observer.next(this.searchTextGemeinde);
    }).pipe(
      mergeMap((token: string) => {
        return this._typeaheadService.querySearchSource(
          _layer.esriLayer['url'],
          hSearchInfo.headSearchField,
          hSearchInfo.keyField,
          token,
          {
            distinct: true,
            orderBy: [hSearchInfo.headSearchField],
            outFields: [hSearchInfo.headSearchField],
          }
        );
      })
    );

    // define search list Parzellennummer
    this.searchListParzellenNr = new Observable((observer: any) => {
      observer.next(this.searchTextParzelleNr);
    }).pipe(
      mergeMap((token: string) => {
        const _condition = `LOWER(${
          hSearchInfo.headSearchField
        }) = '${this.gemeindeRef.nativeElement.value.toLocaleLowerCase()}'`;
        return this._typeaheadService.querySearchSource(
          _layer.esriLayer['url'],
          hSearchInfo.searchField,
          hSearchInfo.keyField,
          token,
          {
            where: _condition,
          }
        );
      })
    );
  }

  /**
   * @name searchParzelle
   * @description search parzellen based on gemeinde and parzellennr.
   */

  public searchParzelle() {
    this.selectionService.removeAllSelectedGraphics();
    this.mainMapService.setCursor(CONSTANTS_VALUE.CURSOR.wait);
    const hSearchInfo = this.getSearchConfig();
    const _layer = this._layerContainerService.layerList.filter(
      f => f.esriLayer.id === hSearchInfo.fkLayer
    )[0];
    // define query
    let where = `${hSearchInfo.headSearchField} ='${this.gemeindeRef.nativeElement.value}'`;
    where = where + ` AND ${hSearchInfo.searchField}='${this.parzelleRef.nativeElement.value}'`;

    const query: __esri.Query = this._queryService.buildQuery(true, where);
    const errorCode = this.CONST.LAYER_ERROR.GET_FEATURES_FROM_LAYER_FAILED;
    // execute query
    this._queryService.execute(_layer.esriLayer['url'], query, errorCode).subscribe(result => {
      if (result.features.length === 0) {
        this.mainMapService.setCursor(CONSTANTS_VALUE.CURSOR.default);
        this._messageService.warning('NOTIFICATIONS.WARNING.SEARCH_HIERARCHY');
        return;
      }
      this.selectionService.zoomOnFeature(result.features[0]);
      this.selectionService.addSelectedGraphic(result.features[0]);
      this.mainMapService.setCursor(CONSTANTS_VALUE.CURSOR.default);
    });
  }

  public clearParzellenField() {
    this._domUtilService.clearInputFieldById(this.parzelleRef.nativeElement.id);
  }

  /**
   * @function getSearchConfig
   * @returns {string} search config infos
   * @description Get the hierachy search configuration for current workspace
   */
  private getSearchConfig(): any {
    return this.searchConfig;
  }

  /**
   * @function setInitValues
   * @param id
   * @param expand
   * @description Sets the initial values
   */
  public setInitValues(id: string, expand: boolean) {
    super.setInitValues(id, expand);
    this.searchConfig = this.getSearchConfigOnComponentId(id);
  }

  private getSearchConfigOnComponentId(id: string): SearchInfo {
    if (id === this.CONST.WIDGET_ID.SearchParzelleComponent) {
      return this._configService.searchConfig.hierarchicalSearch.find(
        t => t.id === 'search_parzelle_sbb'
      );
    } else if (id === this.CONST.WIDGET_ID.SearchParzelleCHComponent) {
      return this._configService.searchConfig.hierarchicalSearch.find(
        t => t.id === 'search_parzelle_ch'
      );
    }
  }
}
